import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Elements } from '@stripe/react-stripe-js'
import { Check, CalendarDays } from 'lucide-react'
import { motion } from '@/components/framer-motion'
import { Checkbox } from '@/components/ui/checkbox'
import {
  OnboardingUserType,
  PLAN_TIERS,
  recommendPlanTierByDoors,
  type BillingConfigResponse,
  type PlanTier,
  type SubscriptionResponse,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { StripeCardForm } from '@/components/billing/StripeCardForm'
import { billingClient } from '@/lib/api/billing.client'
import { stripePromise } from '@/lib/stripe'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep, previousStep } from '../flow.config'
import { PathPromise } from '../components/PathPromise'
import { TIER_SELECTED_CLASSES } from '../tierColors'
import { cn } from '@/lib/utils'

/**
 * A subscription in either of these states means the caller is already paying
 * (or trialing) — the step should skip card capture and offer a Continue CTA.
 */
const RESUMABLE_STATES = new Set(['trialing', 'active'])

function trialDaysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null
  const diffMs = new Date(trialEndsAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
}

export function BillingStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { state } = useOnboarding()

  const [config, setConfig] = useState<BillingConfigResponse | null>(null)
  const [subscription, setSubscription] = useState<
    SubscriptionResponse | null | undefined
  >(undefined)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsShakeCount, setTermsShakeCount] = useState(0)
  const [termsError, setTermsError] = useState(false)

  const recommendedTier = useMemo(
    () => recommendPlanTierByDoors(state.doors ?? 0),
    [state.doors],
  )
  // Fall back to the door recommendation if the tier param is missing or
  // garbage — the plan step passes a valid one, but a malformed URL
  // shouldn't crash the render.
  const tierParam = searchParams.get('tier')
  const selectedTier: PlanTier = useMemo(
    () => PLAN_TIERS.find(t => t.id === tierParam) ?? recommendedTier,
    [tierParam, recommendedTier],
  )
  const selectedTierId = selectedTier.id

  useEffect(() => {
    if (termsAccepted && termsError) setTermsError(false)
  }, [termsAccepted, termsError])

  useEffect(() => {
    if (!termsError) return
    const t = window.setTimeout(() => setTermsError(false), 2000)
    return () => window.clearTimeout(t)
  }, [termsError, termsShakeCount])

  useEffect(() => {
    let cancelled = false
    Promise.all([billingClient.getConfig(), billingClient.getSubscription()])
      .then(([cfg, sub]) => {
        if (cancelled) return
        setConfig(cfg)
        setSubscription(sub)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load billing state', err)
        setSubscription(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const advanceToNextStep = () => {
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, state.userType ?? null) : null
    if (next) navigate(next.route)
  }

  if (subscription === undefined) {
    return <BillingSkeleton />
  }

  const hasActiveSubscription =
    subscription != null &&
    subscription.status != null &&
    RESUMABLE_STATES.has(subscription.status)

  // Standard-path lead capture: card is on file but no subscription exists
  // yet (creation is deferred to sales conversion). We still want to
  // resume with a confirmation state, not re-prompt for card capture.
  const hasCardOnly =
    subscription != null &&
    subscription.card != null &&
    subscription.stripeSubscriptionId == null

  if (hasActiveSubscription || hasCardOnly) {
    return (
      <ActiveSubscriptionState
        subscription={subscription}
        onContinue={advanceToNextStep}
      />
    )
  }

  const userType = state.userType ?? OnboardingUserType.SDK_BUILDER
  const isStandard = userType === OnboardingUserType.STANDARD_USER
  const header = isStandard
    ? 'Save a card on file'
    : 'Add a card to start your trial'
  const ctaLabel = isStandard ? 'Save card only' : 'Start trial'
  const trialDays = config?.trialDays ?? 5
  const proratedPreview = proratedPreviewText(trialDays)

  return (
    <Elements stripe={stripePromise}>
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
        <div className="space-y-2 text-center">
          <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
            SUPER · BILLING
          </p>
          <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
            {header}
          </h1>
          <p className="text-muted-foreground text-sm">No charge today.</p>
        </div>

        <div
          className={cn(
            'flex items-center justify-between rounded-md border p-4',
            TIER_SELECTED_CLASSES[selectedTierId],
          )}
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
              Your plan
            </span>
            <span className="font-space-grotesk text-lg font-semibold tracking-tight">
              {selectedTier.label} · ${selectedTier.priceUsd}
              <span className="text-muted-foreground text-xs font-normal">
                /mo
              </span>
            </span>
            <span className="text-muted-foreground text-xs">
              {selectedTier.includedMinutes.toLocaleString()} min ·{' '}
              {selectedTier.includedSms.toLocaleString()} SMS included
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              const current = findStepByRoute(location.pathname)
              const prev = current
                ? previousStep(current, state.userType ?? null)
                : null
              const route = prev?.route ?? '/onboarding/plan'
              navigate(`${route}?tier=${selectedTierId}`)
            }}
            className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
          >
            ← Change plan
          </button>
        </div>

        <div className="-mt-3">
          <PathPromise
            userType={userType}
            trialDays={config?.trialDays ?? null}
          />
        </div>

        <div className="mt-4">
          <StripeCardForm
            submitSetupIntent={setupIntentId =>
              billingClient.createSubscription({
                setupIntentId,
                planTierId: selectedTierId,
              })
            }
            onSuccess={() => advanceToNextStep()}
            onBeforeSubmit={() => {
              if (termsAccepted) return true
              setTermsShakeCount(c => c + 1)
              setTermsError(true)
              return false
            }}
            ctaLabel={ctaLabel}
            ctaHint="We will not charge your card"
          />
        </div>

        <div className="-mt-3 flex flex-col gap-3">
          {!isStandard && (
            <p className="text-muted-foreground ml-3 text-xs">
              {proratedPreview}
            </p>
          )}

          <motion.label
            key={termsShakeCount}
            animate={
              termsShakeCount > 0
                ? { x: [0, -6, 6, -6, 6, -3, 3, 0] }
                : { x: 0 }
            }
            transition={{ duration: 0.4 }}
            className={cn(
              'ml-3 flex items-start gap-2 text-xs transition-colors duration-300',
              termsError ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            <Checkbox
              checked={termsAccepted}
              onCheckedChange={next => setTermsAccepted(next === true)}
              className={cn(
                'mt-0.5 transition-colors duration-300',
                termsError &&
                  'border-destructive data-[state=checked]:bg-destructive',
              )}
            />
            <span>
              I agree to the{' '}
              <a
                className={cn(
                  'underline underline-offset-2',
                  termsError ? 'text-destructive' : 'text-foreground',
                )}
                href="https://www.hiresuper.com/terms-conditions"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                className={cn(
                  'underline underline-offset-2',
                  termsError ? 'text-destructive' : 'text-foreground',
                )}
                href="https://www.hiresuper.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              .
            </span>
          </motion.label>
        </div>
      </div>
    </Elements>
  )
}

function proratedPreviewText(trialDays: number): string {
  const now = new Date()
  const trialEnd = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000)
  const nextFirst = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
  )
  return `With a ${trialDays}-day free trial, your prorated subscription begins on ${formatLongDate(trialEnd.toISOString())}. Your full monthly subscription starts on ${formatLongDate(nextFirst.toISOString())}.`
}

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  })
}

function ActiveSubscriptionState({
  subscription,
  onContinue,
}: {
  subscription: SubscriptionResponse
  onContinue: () => void
}) {
  const daysLeft = trialDaysRemaining(subscription.trialEndsAt)
  const isTrialing = subscription.status === 'trialing'
  const isCardOnly = subscription.stripeSubscriptionId == null

  const header = isCardOnly
    ? "You've saved your card"
    : isTrialing
      ? "You're on your Super trial"
      : 'Your subscription is active'

  const sub = isCardOnly
    ? 'Our team will reach out to complete your onboarding shortly.'
    : isTrialing && daysLeft !== null
      ? daysLeft === 0
        ? 'Your trial ends today.'
        : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your trial.`
      : 'Manage your card and plan from Settings anytime.'

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
      <div className="space-y-2 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · BILLING
        </p>
        <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
          {header}
        </h1>
        <p className="text-muted-foreground text-sm">{sub}</p>
      </div>

      <div className="border-border flex items-start gap-3 rounded-md border p-4">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
          <Check className="h-3.5 w-3.5" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Card on file</span>
          <span className="text-muted-foreground text-xs">
            You can update it or cancel anytime from Settings.
          </span>
        </div>
      </div>

      {isTrialing &&
        subscription.trialEndsAt &&
        subscription.firstCycleEndsAt && (
          <div className="border-border flex items-start gap-3 rounded-md border p-4">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              <CalendarDays className="h-3.5 w-3.5" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">What happens next</span>
              <span className="text-muted-foreground text-xs">
                Your prorated subscription begins on{' '}
                {formatLongDate(subscription.trialEndsAt)}. Your full monthly
                subscription starts on{' '}
                {formatLongDate(subscription.firstCycleEndsAt)}. Cancel before
                your trial ends and you won't be charged.
              </span>
            </div>
          </div>
        )}

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-billing"
        priority={10}
      >
        <div className="pointer-events-auto">
          <Button onClick={onContinue} size="sm">
            Finish →
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}

function BillingSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
      <div className="space-y-2 text-center">
        <div className="bg-secondary/50 mx-auto h-3 w-24 animate-pulse rounded" />
        <div className="bg-secondary/50 mx-auto h-8 w-72 animate-pulse rounded" />
        <div className="bg-secondary/50 mx-auto h-4 w-96 animate-pulse rounded" />
      </div>
      <div className="bg-secondary/50 h-24 animate-pulse rounded" />
    </div>
  )
}
